/**
 * Dynamic Drawer Helper — canonical pattern for read-only AJAX drawers.
 *
 * Mirrors ``createDynamicModal`` (utils/dynamic-modal.js) but for the
 * right-sidebar slide-in drawer pattern used by the
 * ``components/_*_drawer.html`` partials (visit / cleaner / job /
 * service type detail).
 *
 * Contract:
 *   - Drawer shell is static in the page; ``#${contentId}`` holds a
 *     spinner placeholder.
 *   - On ``open(url, title?)``: GET ``url`` with ``X-Requested-With``,
 *     inject response HTML into ``#${contentId}``, fire
 *     ``onContentInjected``.
 *   - Close paths: any ``[data-drawer-close]`` button inside the
 *     drawer shell, the backdrop click, the Escape key (only when
 *     this drawer is the visible one).
 *   - Body scroll-lock is added on open, removed on close.
 *
 * Required drawer shell HTML (see _cleaner_drawer.html for
 * canonical example):
 *
 *   <div id="myDrawer"
 *        data-auto-init="myDrawer"
 *        class="fixed right-0 top-0 z-50 ... translate-x-full ...">
 *     <div class="flex items-center justify-between ...">  {# header #}
 *       <h4 id="myDrawerLabel">…</h4>
 *       <span id="myDrawerStatusBadge"></span>
 *       <button type="button" data-drawer-close>×</button>
 *     </div>
 *     <div id="myDrawerContent" class="flex-1 overflow-y-auto">
 *       <!-- spinner placeholder; replaced by fetch -->
 *     </div>
 *   </div>
 *   <div id="myDrawerBackdrop" class="hidden fixed inset-0 z-40 ..."></div>
 *
 * The close transition relies on the ``translate-x-full`` Tailwind
 * class being present on the drawer when closed and removed when
 * open. The ``transition-transform`` class is expected to live on
 * the shell so the slide animates.
 */

const SPINNER_HTML = `
  <div class="flex items-center justify-center py-16">
    <svg class="animate-spin h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  </div>
`;

// Single document-level Escape listener for the SPA lifetime, in line
// with ``hx-boost-init.mdc``. Only one drawer is ever visible at a
// time in the app (body scroll-lock + backdrop would conflict
// otherwise), so a single ref is enough; ``open()`` sets it,
// ``close()`` clears it.
let activeDrawer = null;
let escapeBound = false;

function bindEscapeOnce() {
  if (escapeBound) return;
  escapeBound = true;
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (activeDrawer) activeDrawer.close();
  });
}

// Drawer state is ephemeral UI and must never survive history
// navigation — same contract as dynamic-modal.js (see the history
// hooks there for the full rationale). ``close()`` is already fully
// synchronous for drawers, so htmx:beforeHistorySave can reuse it
// directly; htmx:historyRestore sweeps zombie shells restored from
// snapshots taken before this hook existed (innerHTML-only restore =
// no listeners = dead close buttons).
let historyHooksBound = false;

function sweepOpenShells() {
  // Scope to #main-content — the hx-history-elt snapshot boundary.
  // Ephemeral-UI elements outside it (the assistant slide-over and
  // its backdrop, the global confirm modal) are never snapshotted
  // and deliberately survive navigation; leave them alone.
  const root = document.getElementById('main-content');
  if (!root) return;
  // Drawer shells are anchored by their static [data-drawer-close]
  // button; closed state = ``translate-x-full`` present.
  let sweptAny = false;
  root.querySelectorAll('[data-drawer-close]').forEach((btn) => {
    const shell = btn.closest('div[id][class*="fixed"]');
    if (shell && !shell.classList.contains('translate-x-full')) {
      shell.classList.add('translate-x-full');
      sweptAny = true;
    }
  });
  // Their paired full-screen backdrops (``…Backdrop`` / ``…-backdrop``
  // ids, anatomy in the module docstring).
  root
    .querySelectorAll('div[id*="ackdrop"][class*="inset-0"]:not(.hidden)')
    .forEach((b) => { b.classList.add('hidden'); sweptAny = true; });
  if (sweptAny) document.body.classList.remove('overflow-hidden');
}

function bindHistoryHooksOnce() {
  if (historyHooksBound) return;
  historyHooksBound = true;
  document.body.addEventListener('htmx:beforeHistorySave', () => {
    if (activeDrawer) activeDrawer.close();
    sweepOpenShells();
  });
  document.body.addEventListener('htmx:historyRestore', () => {
    sweepOpenShells();
  });
}

// Bind at module-evaluation time (single app bundle → runs once per
// tab), NOT inside createDynamicDrawer: the bespoke drawers that
// don't use this helper (visit-drawer.js and friends) share the same
// [data-drawer-close] shell anatomy and need the sweep too.
bindHistoryHooksOnce();

/**
 * Re-execute <script> tags inside a freshly-injected HTML fragment.
 *
 * Browsers do not run ``<script>`` content set via ``innerHTML``;
 * the legacy inline drawers worked around this by cloning each
 * script into a fresh DOM node, appending to ``document.body``, and
 * removing it after a tick. We preserve that behaviour for the two
 * drawers (cleaner, job) whose returned HTML embeds extra
 * page-specific JS.
 */
function executeNestedScripts(rootEl) {
  const scripts = rootEl.querySelectorAll('script');
  scripts.forEach((script) => {
    const fresh = document.createElement('script');
    if (script.src) {
      fresh.src = script.src;
    } else {
      fresh.textContent = script.textContent;
    }
    document.body.appendChild(fresh);
    setTimeout(() => fresh.remove(), 100);
  });
}

/**
 * Apply "portal" content from a freshly-injected partial into elements
 * that live OUTSIDE the drawer's content area (typically header
 * badges that the partial needs to update from server-side context).
 *
 * Markup contract: the loaded partial includes one or more
 * ``[data-portal-target="<elementId>"]`` containers; for each one we
 * find the destination by id, replace its innerHTML with the source's
 * innerHTML, and remove the source from the content node so it never
 * paints inside the scrollable area.
 *
 * Use case (Phase 4 Vague 3c): server-rendered status badges that
 * used to be painted by an inline ``<script>`` reading Django
 * context. Replaces the badge IIFE in
 * ``templates/office/jobs/partials/_detail_drawer.html`` and the
 * status-badge block in
 * ``templates/office/visits/partials/_detail_drawer.html``.
 */
function applyPortals(rootEl) {
  rootEl.querySelectorAll('[data-portal-target]').forEach((source) => {
    const targetId = source.dataset.portalTarget;
    if (!targetId) return;
    const target = document.getElementById(targetId);
    if (!target) return;
    target.innerHTML = source.innerHTML;
    source.remove();
  });
}

/**
 * Create a dynamic drawer helper bound to a static shell in the DOM.
 *
 * @param {object}   config
 * @param {string}   config.drawerId             — id of the static drawer shell.
 * @param {string}   config.backdropId           — id of the backdrop element.
 * @param {string}   config.contentId            — id of the scrollable content area
 *                                                 (where fetched HTML lands).
 * @param {string}   [config.titleId]            — id of the header title element
 *                                                 (updated on each open if a title
 *                                                 is passed; restored to ``defaultTitle``
 *                                                 on close).
 * @param {string}   [config.badgeId]            — id of the status badge element
 *                                                 (cleared on every open and close).
 * @param {string}   [config.defaultTitle]       — restored into ``#${titleId}`` on close.
 * @param {boolean}  [config.executeNestedScripts=false]
 *                                              — if true, re-execute any <script>
 *                                                tags found in the fetched HTML
 *                                                (legacy behaviour for cleaner/job).
 * @param {function} [config.onContentInjected] — fired after the GET succeeds.
 *                                                 Receives ``(contentEl, helper)``.
 * @param {function} [config.onClose]           — fired right after the drawer is
 *                                                 hidden (any close path).
 *                                                 Receives ``(helper)``.
 * @param {string}   [config.errorLoadingMessage] — text shown if the GET fails.
 * @param {string}   [config.closeButtonText]   — text for the retry/close button on
 *                                                 load failure.
 *
 * @returns {{open: function, close: function, isOpen: function, drawer: HTMLElement}|null}
 */
export function createDynamicDrawer(config) {
  const {
    drawerId,
    backdropId,
    contentId,
    titleId = null,
    badgeId = null,
    defaultTitle = '',
    executeNestedScripts: shouldExecuteScripts = false,
    onContentInjected = null,
    onClose = null,
    errorLoadingMessage = 'Error loading content. Please try again.',
    closeButtonText = 'Close',
  } = config || {};

  const drawer = document.getElementById(drawerId);
  const backdrop = document.getElementById(backdropId);
  const content = document.getElementById(contentId);
  if (!drawer || !backdrop || !content) return null;

  const titleEl = titleId ? document.getElementById(titleId) : null;
  const badgeEl = badgeId ? document.getElementById(badgeId) : null;

  bindEscapeOnce();

  function isOpen() {
    return !drawer.classList.contains('translate-x-full');
  }

  function open(url, titleText = null) {
    activeDrawer = helper;

    if (badgeEl) badgeEl.innerHTML = '';
    if (titleEl) {
      titleEl.textContent = titleText || defaultTitle || titleEl.textContent;
    }
    content.innerHTML = SPINNER_HTML;

    backdrop.classList.remove('hidden');
    drawer.classList.remove('translate-x-full');
    document.body.classList.add('overflow-hidden');

    fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Drawer load failed (${response.status})`);
        return response.text();
      })
      .then((html) => {
        content.innerHTML = html;
        // Move any [data-portal-target] containers OUT of content
        // and into their named destination (e.g. header badges).
        applyPortals(content);
        if (shouldExecuteScripts) {
          executeNestedScripts(content);
        }
        if (typeof onContentInjected === 'function') {
          onContentInjected(content, helper);
        }
      })
      .catch((err) => {
        console.error(`Error loading drawer ${drawerId}:`, err);
        content.innerHTML = `
          <div class="text-center py-16 text-red-500 px-4">
            <p>${errorLoadingMessage}</p>
            <button type="button" data-drawer-close class="mt-4 btn-secondary">${closeButtonText}</button>
          </div>
        `;
        // The error-state close button lives inside the freshly
        // injected content node — wire it to ``close()`` since the
        // delegated handler installed below only catches buttons that
        // existed at construction time.
        content.querySelectorAll('[data-drawer-close]').forEach((btn) => {
          if (!btn._drawerCloseBound) {
            btn._drawerCloseBound = true;
            btn.addEventListener('click', close);
          }
        });
      });
  }

  function close() {
    drawer.classList.add('translate-x-full');
    backdrop.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');

    if (badgeEl) badgeEl.innerHTML = '';
    if (titleEl && defaultTitle) titleEl.textContent = defaultTitle;

    if (activeDrawer === helper) activeDrawer = null;

    if (typeof onClose === 'function') {
      onClose(helper);
    }
  }

  // Per-element close handlers: the static close button(s) inside
  // the drawer shell, plus a click on the backdrop. Use plain JS
  // properties (not dataset.*) so the bind flag is not serialised
  // into HTMX history snapshots — see hx-boost-init.mdc § 3.
  drawer.querySelectorAll('[data-drawer-close]').forEach((btn) => {
    if (btn._drawerCloseBound) return;
    btn._drawerCloseBound = true;
    btn.addEventListener('click', close);
  });
  if (!backdrop._drawerCloseBound) {
    backdrop._drawerCloseBound = true;
    backdrop.addEventListener('click', close);
  }

  const helper = { open, close, isOpen, drawer };
  return helper;
}
